import {Injectable} from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import {Observable, of} from 'rxjs';
import {catchError, map, switchMap} from 'rxjs/operators';
import {AuthService} from '../services/auth.service';
import { ProcessService } from '../services/process.service';

@Injectable({
    providedIn: 'root'
})

export class ControlModuleGuard  {
    // Roles that may reach the control module regardless of process assignment.
    private static readonly ALLOWED_ROLES = [
        'ADMIN', 'CONTROLLER', 'DIRECTOR_APPROVER', 'DIRECTOR_GENERAL',
        'COORDINATOR', 'INSPECTOR', 'CHEF'
    ];

    constructor(private authService: AuthService, private router: Router, private processService: ProcessService) {
    }

    // This previously subscribed to getCurrentUser() and then fell through to an
    // unconditional `return of(true)`. The of(true)/of(false) written inside the
    // subscribe callbacks returned from the callback, not from canActivate, so
    // they were discarded and the guard granted access to everyone: the role
    // list and the isAssigned() fallback gated nothing. Returning the observable
    // is what makes the router actually wait for the decision.
    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.authService.getCurrentUser().pipe(
            switchMap(usr => {
                if (this.hasControlRole(usr)) {
                    return of(true);
                }
                // No control-module role, but the user may still be individually
                // assigned to this process. That was the original intent.
                return this.processService.isAssigned().pipe(
                    map(res => this.allowOrRedirect(!!res?.is_assigned)),
                    catchError(() => of(this.allowOrRedirect(false)))
                );
            }),
            catchError(() => of(this.allowOrRedirect(false)))
        );
    }

    // roles may arrive as an array or as a delimited string; Array.includes and
    // String.includes both work here, which preserves the original matching
    // behaviour. A missing roles claim is not an error: it fails the role check
    // and falls through to the isAssigned() fallback rather than hard-denying.
    private hasControlRole(usr: unknown): boolean {
        const roles = (usr as { roles?: string[] | string } | null)?.roles;
        if (!roles) {
            return false;
        }
        return ControlModuleGuard.ALLOWED_ROLES.some(role => roles.includes(role));
    }

    private allowOrRedirect(allowed: boolean): boolean {
        if (!allowed) {
            this.router.navigate(['/']);
        }
        return allowed;
    }
}
